import { useEffect, useMemo, useRef, useState } from "react";
import {
  api,
  ApiError,
  type ConfigDocumentos,
  type PessoaRh,
  type ResponsavelDocumento,
  type SetorConfig,
} from "../api";
import { IconClose, IconPlus, IconTrash } from "../icons";

/** Linha da configuração que vale para os setores sem ninguém marcado. */
const PADRAO = "*";

/**
 * A coluna "Revisa" saiu em 22/10/2026: a revisão passou a ser de quem elabora.
 * Quem estava marcado para revisar continua guardado e volta igual ao salvar.
 */
type Papel = "aprovador" | "publicador";
type Aba = "responsaveis" | "tipos";

/** As colunas da tabela, na ordem do fluxo do documento. */
const PAPEIS: { papel: Papel; titulo: string; quem: string; verbo: string }[] = [
  { papel: "aprovador", titulo: "Aprova", quem: "sugeridos no cadastro", verbo: "aprova" },
  { papel: "publicador", titulo: "Publica", quem: "colaborador da Qualidade", verbo: "publica" },
];

/** Tipo em edição: os meses ficam como texto (em branco = o tipo não vence). */
interface TipoEdit {
  /** Chave da linha na tela (o id só existe depois de salvo). */
  linha: string;
  id?: number;
  nome: string;
  sigla: string;
  /** Sigla gravada — se mudar, o "próximo código" do servidor deixa de valer. */
  siglaSalva: string | null;
  meses: string;
  ativo: boolean;
  documentos: number;
  proximo: string | null;
}

/**
 * Quem vale quando ninguém (ativo) está marcado — a MESMA ordem do servidor
 * (montarResolvedor em server/documentos-config.ts): o Padrão, senão o RH
 * (para aprovar, o gestor do setor e os diretores; para publicar, o setor
 * Qualidade), senão o administrador.
 */
interface Automatico {
  /** De onde vem: é a etiqueta cinza antes dos nomes. */
  origem: "Padrão" | "RH" | "Admin";
  /** Quem é, em palavras (quando não há um nome só: "gestor de cada setor"). */
  texto: string | null;
  nomes: string[];
  /** A frase inteira (dica ao passar o mouse). */
  explicacao: string;
}

/** Texto comparável: sem acento, sem diferença de maiúscula e de espaços. */
const chave = (v: unknown) =>
  String(v ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

/** "Fulano" · "Fulano e Beltrano" · "Fulano, Beltrano e Ciclano". */
function nomes(lista: string[]): string {
  return lista.length <= 1 ? lista.join("") : `${lista.slice(0, -1).join(", ")} e ${lista[lista.length - 1]}`;
}

let linhaNova = 0;

interface Props {
  onClose: () => void;
  /** Configuração gravada: a lista recarrega (tipos, códigos e quem decide mudaram). */
  onSalvo: () => void;
}

/**
 * Pop-up "Configurar" da aba Documentos (só administrador).
 *
 * Quem aprova e publica: UMA tabela — cada setor do RH é uma linha e as
 * colunas Aprova / Publica ficam alinhadas, com o cabeçalho preso no topo
 * (pedido de 22/10/2026: a versão em cartões, com um texto de regra diferente
 * em cada coluna, deixava os campos desencontrados). Em cada célula: as
 * pessoas marcadas ou, em cinza tracejado, quem vale sozinho e de onde vem
 * (Padrão, RH ou administrador). A primeira linha é o Padrão, que vale para os
 * setores sem ninguém marcado.
 *
 * "Aprova" são os SUGERIDOS: quem cadastra o documento escolhe a pessoa que
 * aprova, e os daqui aparecem primeiro na lista (e aprovam os documentos de
 * antes, que não têm ninguém escolhido). "Publica" decide a publicação —
 * basta um dos marcados.
 *
 * Tipos e códigos: a mesma tabela — a sigla dá o código automático (PR →
 * PR-001) e "revisar a cada" diz quando a versão vigente precisa ser revista.
 *
 * Tudo é gravado de uma vez no "Salvar configuração" (o servidor confere de novo).
 */
export default function DocumentosConfigModal({ onClose, onSalvo }: Props) {
  const [config, setConfig] = useState<ConfigDocumentos | null>(null);
  const [erroCarga, setErroCarga] = useState<string | null>(null);
  const [aba, setAba] = useState<Aba>("responsaveis");
  const [tipos, setTipos] = useState<TipoEdit[]>([]);
  const [marcados, setMarcados] = useState<ResponsavelDocumento[]>([]);
  const [alterado, setAlterado] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const corpoRef = useRef<HTMLDivElement>(null);

  // Trocar de aba começa do topo (a rolagem da outra aba não serve para esta).
  useEffect(() => {
    if (corpoRef.current) corpoRef.current.scrollTop = 0;
  }, [aba]);

  useEffect(() => {
    let vivo = true;
    api.documentos
      .config()
      .then((c) => {
        if (!vivo) return;
        setConfig(c);
        setMarcados(c.responsaveis);
        setTipos(
          c.tipos.map((t) => ({
            linha: `t${t.id}`,
            id: t.id,
            nome: t.nome,
            sigla: t.sigla,
            siglaSalva: t.sigla,
            meses: t.revisar_meses == null ? "" : String(t.revisar_meses),
            ativo: t.ativo,
            documentos: t.documentos ?? 0,
            proximo: t.proximo ?? null,
          })),
        );
      })
      .catch((err) => {
        if (vivo) setErroCarga(err instanceof ApiError ? err.message : "Não foi possível carregar a configuração.");
      });
    return () => {
      vivo = false;
    };
  }, []);

  const pessoaPorId = useMemo(() => new Map((config?.pessoas ?? []).map((p) => [p.rh_id, p])), [config]);
  /** Quem saiu do RH continua na lista (com aviso), mas não decide mais. */
  const valendo = (r: ResponsavelDocumento) => !(r.rh_id != null && pessoaPorId.get(r.rh_id)?.ativo === false);

  const doSetor = (setor: string, papel: Papel) =>
    marcados.filter(
      (r) => r.papel === papel && (setor === PADRAO ? r.departamento === PADRAO : r.departamento !== PADRAO && chave(r.departamento) === chave(setor)),
    );

  function marcar(setor: string, papel: Papel, rhId: number) {
    const p = pessoaPorId.get(rhId);
    if (!p || doSetor(setor, papel).some((r) => r.rh_id === rhId)) return;
    setMarcados((l) => [...l, { departamento: setor, papel, rh_id: p.rh_id, nome: p.nome, email: p.email }]);
    setAlterado(true);
  }

  function tirar(r: ResponsavelDocumento) {
    setMarcados((l) => l.filter((x) => x !== r));
    setAlterado(true);
  }

  /** Quem decide quando ninguém (ativo) foi marcado — a mesma ordem do servidor. */
  function automatico(setor: SetorConfig | null, papel: Papel): Automatico {
    const padrao = doSetor(PADRAO, papel).filter(valendo).map((r) => r.nome);
    const diretores = config?.diretoresRh ?? [];
    const qualidade = config?.qualidadeRh ?? [];
    const admin = (explicacao: string): Automatico => ({ origem: "Admin", texto: "administrador", nomes: [], explicacao });

    if (setor === null) {
      if (papel === "aprovador") {
        return {
          origem: "RH",
          texto: diretores.length > 0 ? "gestor do setor ou diretor:" : "gestor do setor",
          nomes: diretores,
          explicacao: `Ninguém marcado: sugere o gestor do setor no RH e os diretores${
            diretores.length > 0 ? ` (${nomes(diretores)})` : ""
          }; sem eles, o administrador.`,
        };
      }
      return qualidade.length > 0
        ? {
            origem: "RH",
            texto: "setor Qualidade:",
            nomes: qualidade,
            explicacao: `Ninguém marcado: publica quem é do setor Qualidade no RH (${nomes(qualidade)}).`,
          }
        : admin("Ninguém marcado: publica quem é do setor Qualidade no RH; sem ninguém lá, o administrador.");
    }

    if (padrao.length > 0) {
      return { origem: "Padrão", texto: null, nomes: padrao, explicacao: `Ninguém marcado: vale o Padrão (${nomes(padrao)}).` };
    }
    if (papel === "aprovador") {
      const quem = [...new Set([...setor.gestoresRh, ...diretores])];
      return quem.length > 0
        ? {
            origem: "RH",
            texto: null,
            nomes: quem,
            explicacao: `Ninguém marcado: sugere o gestor do setor e os diretores (${nomes(quem)}).`,
          }
        : admin("Ninguém marcado e ninguém no RH para sugerir: documento sem ninguém escolhido fica com o administrador.");
    }
    return qualidade.length > 0
      ? {
          origem: "RH",
          texto: null,
          nomes: qualidade,
          explicacao: `Ninguém marcado: publica quem é do setor Qualidade no RH (${nomes(qualidade)}).`,
        }
      : admin("Ninguém marcado e ninguém do setor Qualidade no RH: publica o administrador.");
  }

  function mudarTipo(linha: string, mudanca: Partial<TipoEdit>) {
    setTipos((l) => l.map((t) => (t.linha === linha ? { ...t, ...mudanca } : t)));
    setAlterado(true);
  }

  function novoTipo() {
    linhaNova += 1;
    setTipos((l) => [
      ...l,
      { linha: `n${linhaNova}`, nome: "", sigla: "", siglaSalva: null, meses: "24", ativo: true, documentos: 0, proximo: null },
    ]);
    setAlterado(true);
  }

  function removerTipo(t: TipoEdit) {
    setTipos((l) => l.filter((x) => x.linha !== t.linha));
    setAlterado(true);
  }

  function fechar() {
    if (salvando) return;
    if (alterado && !confirm("Fechar sem salvar? As mudanças feitas aqui serão perdidas.")) return;
    onClose();
  }

  async function salvar() {
    // Conferência rápida na tela — o servidor confere tudo de novo.
    const semNome = tipos.find((t) => !t.nome.trim());
    const semSigla = tipos.find((t) => !t.sigla);
    if (semNome || semSigla) {
      setAba("tipos");
      setErro(semNome ? "Todo tipo de documento precisa de um nome." : `Falta a sigla do tipo "${semSigla!.nome}".`);
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      await api.documentos.salvarConfig({
        tipos: tipos.map((t) => ({
          id: t.id,
          nome: t.nome.trim(),
          sigla: t.sigla,
          revisar_meses: t.meses.trim() === "" ? null : Number(t.meses),
          ativo: t.ativo,
        })),
        responsaveis: marcados,
      });
      onSalvo();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível salvar a configuração.");
    } finally {
      setSalvando(false);
    }
  }

  const ativas = useMemo(() => (config?.pessoas ?? []).filter((p) => p.ativo), [config]);

  /** Quantos setores têm alguém marcado em pelo menos uma etapa (o resto segue o automático). */
  const comMarcados = (config?.setores ?? []).filter((s) =>
    PAPEIS.some(({ papel }) => doSetor(s.nome, papel).some(valendo)),
  ).length;

  return (
    <div className="modal-overlay" onMouseDown={fechar}>
      <div className="modal-card modal-lg cfgm-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Configuração dos documentos</h3>
          <button type="button" className="icon-btn" title="Fechar" onClick={fechar}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body cfg-doc" ref={corpoRef}>
          {erroCarga ? (
            <div className="form-error">{erroCarga}</div>
          ) : !config ? (
            <p className="doc-espera">Carregando…</p>
          ) : (
            <>
              <div className="almox-tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={aba === "responsaveis"}
                  className={`almox-tab ${aba === "responsaveis" ? "active" : ""}`}
                  onClick={() => setAba("responsaveis")}
                >
                  Quem aprova e publica
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={aba === "tipos"}
                  className={`almox-tab ${aba === "tipos" ? "active" : ""}`}
                  onClick={() => setAba("tipos")}
                >
                  Tipos e códigos
                </button>
              </div>

              {aba === "responsaveis" ? (
                <>
                  <div className="cfgm-intro">
                    <p className="doc-ajuda">
                      Cada linha é um setor do RH. Quem cadastra o documento faz a elaboração e a revisão e escolhe
                      quem aprova: os marcados em <strong>Aprova</strong> aparecem primeiro nessa lista, como
                      sugeridos. Em <strong>Publica</strong>, basta um dos marcados publicar — só então o documento
                      passa a valer. Sem ninguém marcado, vale quem aparece em cinza: o <strong>Padrão</strong>{" "}
                      (primeira linha), senão o RH; sem ninguém no RH, o administrador.
                    </p>
                    <div className="cfgm-legenda" aria-hidden="true">
                      <span className="cfg-chip cfgm-amostra">
                        <span>Marcado</span>
                      </span>
                      <span className="cfgm-auto cfgm-amostra">
                        <span className="cfgm-origem">RH</span>
                        <span>Automático</span>
                      </span>
                      <small>
                        {comMarcados} de {config.setores.length} setor{config.setores.length === 1 ? "" : "es"} com
                        alguém marcado
                      </small>
                    </div>
                  </div>
                  {!config.rhOnline && (
                    <div className="crud-aviso">
                      <strong>O RH não respondeu agora.</strong> A lista de pessoas está vazia e os setores são os que
                      este sistema já conhecia. Dá para salvar os tipos; para marcar pessoas, feche e abra de novo
                      em instantes.
                    </div>
                  )}

                  <div className="cfgm" role="table" aria-label="Quem aprova e publica, por setor">
                    <div className="cfgm-cab" role="row">
                      <span className="cfgm-cab-setor" role="columnheader">
                        Setor
                      </span>
                      {PAPEIS.map((p) => (
                        <span key={p.papel} role="columnheader">
                          <strong>{p.titulo}</strong>
                          <small>{p.quem}</small>
                        </span>
                      ))}
                    </div>
                    {[null, ...config.setores].map((setor) => {
                      const nome = setor?.nome ?? PADRAO;
                      return (
                        <div key={nome} className={`cfgm-linha${setor === null ? " cfgm-padrao" : ""}`} role="row">
                          <div className="cfgm-setor" role="rowheader">
                            <strong>{setor ? setor.nome : "Padrão"}</strong>
                            <small className={setor?.foraDoRh ? "cfgm-fora" : undefined}>
                              {setor
                                ? setor.foraDoRh
                                  ? "ninguém cadastrado no RH"
                                  : `${setor.documentos} documento${setor.documentos === 1 ? "" : "s"}`
                                : "vale para os setores sem ninguém marcado"}
                            </small>
                          </div>
                          {PAPEIS.map(({ papel, titulo, verbo }) => {
                            const lista = doSetor(nome, papel);
                            return (
                              <CelulaPapel
                                key={papel}
                                titulo={titulo}
                                verbo={verbo}
                                setor={setor ? setor.nome : "Padrão"}
                                lista={lista}
                                pessoas={ativas}
                                pessoaPorId={pessoaPorId}
                                auto={lista.some(valendo) ? null : automatico(setor, papel)}
                                onMarcar={(rhId) => marcar(nome, papel, rhId)}
                                onTirar={tirar}
                              />
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <>
                  <p className="doc-ajuda">
                    O código do documento novo é a <strong>sigla</strong> do tipo + número em sequência (ex.: PR-001,
                    PR-002). Quem cadastra pode digitar outro código; o sistema não deixa repetir.{" "}
                    <strong>Revisar a cada</strong> diz quando a versão vigente precisa ser revista, contando da
                    aprovação — a lista avisa {config.avisoDias} dias antes. Em branco, o tipo não vence (ex.:
                    Registro).
                  </p>
                  <div className="cfgm cfgm-tipos" role="table" aria-label="Tipos de documento">
                    <div className="cfgm-cab" role="row">
                      <span role="columnheader">Tipo</span>
                      <span role="columnheader">Sigla</span>
                      <span role="columnheader">Revisar a cada</span>
                      <span role="columnheader">Próximo código</span>
                      <span role="columnheader" className="cfgm-num">
                        Docs
                      </span>
                      <span role="columnheader">Ativo</span>
                      <span role="columnheader" aria-label="Remover" />
                    </div>
                    {tipos.map((t) => (
                      <div key={t.linha} className={`cfgm-linha${t.ativo ? "" : " cfgm-inativo"}`} role="row">
                        <div role="cell" className="cfgm-tipo-nome" data-rotulo="Tipo">
                          <input
                            type="text"
                            value={t.nome}
                            maxLength={60}
                            placeholder="Ex.: Procedimento"
                            aria-label="Tipo"
                            onChange={(e) => mudarTipo(t.linha, { nome: e.target.value })}
                          />
                        </div>
                        <div role="cell" data-rotulo="Sigla">
                          <input
                            type="text"
                            value={t.sigla}
                            maxLength={10}
                            placeholder="PR"
                            aria-label="Sigla"
                            onChange={(e) =>
                              mudarTipo(t.linha, { sigla: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })
                            }
                          />
                        </div>
                        <div role="cell" data-rotulo="Revisar a cada" className="cfgm-meses">
                          <input
                            type="number"
                            min={1}
                            max={120}
                            step={1}
                            value={t.meses}
                            placeholder="Não vence"
                            aria-label="Revisar a cada (meses)"
                            onChange={(e) => mudarTipo(t.linha, { meses: e.target.value })}
                          />
                          <span>meses</span>
                        </div>
                        <div role="cell" data-rotulo="Próximo código" className="cfgm-codigo">
                          {!t.sigla ? (
                            <span className="cfgm-falta">escolha a sigla</span>
                          ) : t.proximo && t.sigla === t.siglaSalva ? (
                            <code>{t.proximo}</code>
                          ) : (
                            <span title="Segue o maior número que já existir com esta sigla">
                              <code>{t.sigla}-001</code>…
                            </span>
                          )}
                        </div>
                        <div role="cell" data-rotulo="Documentos" className="cfgm-num">
                          {t.documentos}
                        </div>
                        <div role="cell" data-rotulo="Ativo">
                          <label
                            className="cfg-ativo"
                            title={t.ativo ? "Aparece no cadastro de documento novo" : "Inativo: não aparece no cadastro de documento novo"}
                          >
                            <input
                              type="checkbox"
                              checked={t.ativo}
                              onChange={(e) => mudarTipo(t.linha, { ativo: e.target.checked })}
                            />
                            <span className="cfgm-ativo-texto">{t.ativo ? "Ativo" : "Inativo"}</span>
                          </label>
                        </div>
                        <div role="cell" className="cfgm-remover">
                          <button
                            type="button"
                            className="icon-btn icon-btn-danger"
                            title={
                              t.documentos > 0
                                ? `Remover: não dá, ${t.documentos} documento(s) usam este tipo — desmarque "Ativo"`
                                : "Remover este tipo"
                            }
                            disabled={t.documentos > 0}
                            onClick={() => removerTipo(t)}
                          >
                            <IconTrash size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button type="button" className="btn-secondary btn-sm cfg-novo" onClick={novoTipo}>
                    <IconPlus size={15} /> Novo tipo
                  </button>
                </>
              )}
            </>
          )}
        </div>

        <div className="modal-foot">
          {erro && <div className="form-error">{erro}</div>}
          <button type="button" className="btn-secondary" onClick={fechar} disabled={salvando}>
            {erroCarga ? "Fechar" : "Cancelar"}
          </button>
          {!erroCarga && (
            <button type="button" className="btn-primary" disabled={!config || salvando} onClick={salvar}>
              {salvando ? "Salvando..." : "Salvar configuração"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface CelulaProps {
  /** "Aprova" ou "Publica" (rótulo da célula no celular). */
  titulo: string;
  /** "aprova"… (para o leitor de tela: "Acrescentar quem aprova no Comercial"). */
  verbo: string;
  setor: string;
  lista: ResponsavelDocumento[];
  /** Pessoas ativas do RH que podem ser marcadas. */
  pessoas: PessoaRh[];
  pessoaPorId: Map<number, PessoaRh>;
  /** Quem decide sozinho (null = há alguém marcado e ativo). */
  auto: Automatico | null;
  onMarcar: (rhId: number) => void;
  onTirar: (r: ResponsavelDocumento) => void;
}

/**
 * Uma célula da tabela: quem está marcado (ou quem decide sozinho) à
 * esquerda e o "+" à direita — o "+" fica no mesmo lugar em toda célula, é
 * isso que deixa as colunas alinhadas.
 */
function CelulaPapel({ titulo, verbo, setor, lista, pessoas, pessoaPorId, auto, onMarcar, onTirar }: CelulaProps) {
  const ja = new Set(lista.map((r) => r.rh_id));
  // Agrupadas pelo setor do RH, A→Z (a lista já vem em ordem de nome); sem setor por último.
  const grupos = useMemo(() => {
    const m = new Map<string, PessoaRh[]>();
    for (const p of pessoas) {
      const g = p.departamento || "Sem setor no RH";
      m.set(g, [...(m.get(g) ?? []), p]);
    }
    return [...m.entries()].sort(([a], [b]) =>
      a === "Sem setor no RH" ? 1 : b === "Sem setor no RH" ? -1 : a.localeCompare(b, "pt-BR", { sensitivity: "base" }),
    );
  }, [pessoas]);

  return (
    <div className="cfgm-cel" role="cell" data-rotulo={titulo}>
      <div className="cfgm-quem">
        {lista.map((r) => {
          const p = r.rh_id != null ? pessoaPorId.get(r.rh_id) : undefined;
          const inativo = p?.ativo === false;
          return (
            <span key={`${r.rh_id ?? r.nome}`} className={`cfg-chip${inativo ? " cfg-chip-inativo" : ""}`}>
              <span title={[p?.nome ?? r.nome, p?.cargo].filter(Boolean).join(" — ")}>{p?.nome ?? r.nome}</span>
              {inativo && <em>saiu do RH</em>}
              <button type="button" title={`Tirar ${p?.nome ?? r.nome}`} onClick={() => onTirar(r)}>
                <IconClose size={12} />
              </button>
            </span>
          );
        })}
        {auto && (
          <span className="cfgm-auto" title={auto.explicacao}>
            {/* "Admin administrador" diria a mesma coisa duas vezes. */}
            {auto.origem !== "Admin" && <span className="cfgm-origem">{auto.origem}</span>}
            <span className="cfgm-auto-texto">
              {auto.texto}
              {auto.texto && auto.nomes.length > 0 ? " " : ""}
              {auto.nomes.join(", ")}
            </span>
          </span>
        )}
      </div>
      {pessoas.length > 0 && (
        <label className="cfgm-add" title={`Acrescentar quem ${verbo}`}>
          <IconPlus size={15} />
          <select
            value=""
            aria-label={`Acrescentar quem ${verbo} — ${setor}`}
            onChange={(e) => {
              const id = Number(e.target.value);
              if (id) onMarcar(id);
            }}
          >
            <option value="">Acrescentar pessoa…</option>
            {grupos.map(([grupo, lista]) => (
              <optgroup key={grupo} label={grupo}>
                {lista.map((p) => (
                  <option key={p.rh_id} value={p.rh_id} disabled={ja.has(p.rh_id)}>
                    {p.nome}
                    {p.cargo ? ` — ${p.cargo}` : ""}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
      )}
    </div>
  );
}
